/* eslint-disable react/no-unknown-property */
import {
  Button,
  Icon,
  IconButton,
  Select,
  SelectOption,
  TextCaption,
} from '@pypestream/design-system';
import { FC, Fragment, useEffect, useRef, useState } from 'react';

import {
  AUTO_FAIL,
  criterionHelp,
  multiplier,
  passAt,
  RESPONSE_TARGET_S,
  passes,
  qualityScore,
  scoreExample,
  verdictCriteria,
  type Criterion,
} from '../../utils/evaluation-rubric';
import type { LiveChatState } from './live-chat';
import type { ScenarioStep } from './scenario-run';
import {
  START_POSITIONS,
  channelKey,
  type ExpectedAgent,
  type RunChannel,
  type RunScore,
  type StartPosition,
  type ValidateOption,
  type ValidateTurn,
  type ValidateWebview,
} from './validate-runs';
import { useExpectedAgentEnabled } from './validate-agents';
import { EngineTrace, RunTimeline } from './engine-trace-view';
import { useEngineTrace } from './use-engine-trace';
import { singleSelectValue } from '../../utils/single-select-value';
import { BubbleMarkdown } from './bubble-markdown';
import { EmbedNote } from './embed-note';
import { renderIcons, withoutIcons } from './icon-tokens';
import { useCallTranscript } from './use-call-transcript';
import { callStatusText, useTestCall } from './use-test-call';
import { useHealthCapability } from './use-health-capabilities';
import { VoiceCallGate } from './voice-calls-off-note';
import type { VoiceLine } from './validate-api';
import { WidgetChat } from './widget-chat';
import { webviewFrame } from './webview-frame';

/* ---- Shared ------------------------------------------------------------- */

export const ChannelSelect: FC<{
  channels: RunChannel[];
  value: string | null;
  onChange: (appId: string) => void;
  testId: string;
  disabled?: boolean;
}> = ({ channels, value, onChange, testId, disabled }) => (
  <Select
    test-id={testId}
    variant="outlined"
    size="small"
    value={value ?? ''}
    disabled={disabled}
    onChange={(e) => onChange(singleSelectValue(e.target.value))}
  >
    {channels.map((c) => (
      <SelectOption key={channelKey(c)} value={channelKey(c)}>
        {c.name} · {c.env === 'live' ? 'Live' : 'Sandbox'}
        {c.voiceChannelId ? ' · Voice' : ''}
      </SelectOption>
    ))}
  </Select>
);

/** "Where does it start?" — Beginning / Mid / End. */
export const StartSegments: FC<{
  value: StartPosition;
  onChange: (value: StartPosition) => void;
  testIdPrefix: string;
}> = ({ value, onChange, testIdPrefix }) => (
  <div
    className="c-validate-segments"
    role="radiogroup"
    aria-label="Where does it start?"
  >
    {START_POSITIONS.map((p) => (
      <button
        key={p.value}
        type="button"
        role="radio"
        aria-checked={value === p.value}
        className={value === p.value ? 'is-active' : ''}
        test-id={`${testIdPrefix}-${p.value}`}
        onClick={() => onChange(p.value)}
      >
        {p.label}
      </button>
    ))}
  </div>
);

/** Which agent should handle it. Empty value = not specified. */
export const AgentSelect: FC<{
  agents: ExpectedAgent[];
  value: string | null;
  onChange: (id: string | null) => void;
  testId: string;
  disabled?: boolean;
}> = ({ agents, value, onChange, testId, disabled }) => (
  <Select
    test-id={testId}
    variant="outlined"
    size="small"
    value={value ?? ''}
    disabled={disabled}
    onChange={(e) => onChange(singleSelectValue(e.target.value) || null)}
  >
    <SelectOption value="">Not specified</SelectOption>
    {agents.map((a) => (
      <SelectOption key={a.id} value={a.id}>
        {a.title}
      </SelectOption>
    ))}
  </Select>
);

export const PanelHead: FC<{
  title: string;
  sub?: string;
  icon?: string;
  onClose?: () => void;
  children?: React.ReactNode;
}> = ({ title, sub, icon, onClose, children }) => (
  <div className="c-validate-panel__head">
    <div className="c-validate-panel__head-text">
      <h2 className="c-validate-panel__title">
        {icon && <Icon name={icon as never} size="small" />}
        {title}
        {children}
      </h2>
      {sub && <p className="c-validate-panel__sub">{sub}</p>}
    </div>
    {onClose && (
      <IconButton
        name="close"
        variant="tertiary"
        size="small"
        aria-label="Close"
        onClick={onClose}
      />
    )}
  </div>
);

/** The conversation, as bubbles. Only the live chat's last options are pickable. */
/**
 * One reply's own score, under its bubble: pass/fail at a glance, and on
 * click the scorer's reason and the per-criterion breakdown.
 */
const TurnScore: FC<{ score: RunScore }> = ({ score }) => {
  const [open, setOpen] = useState(false);
  if (score.status === 'idle') return null;
  if (score.status === 'pending') {
    return (
      <span
        className="c-validate-turn-score is-pending"
        test-id="bobcat-validate-turn-score"
      >
        Scoring this reply…
      </span>
    );
  }
  if (score.status === 'error') {
    return (
      <span
        className="c-validate-turn-score is-error"
        test-id="bobcat-validate-turn-score"
        title={score.message}
      >
        Couldn’t score this reply
      </span>
    );
  }
  const quality = qualityScore(score.verdict);
  const ok = passes(score.verdict);
  return (
    <div className="c-validate-turn-score-wrap">
      <button
        type="button"
        className={`c-validate-turn-score ${ok ? 'is-pass' : 'is-fail'}`}
        test-id="bobcat-validate-turn-score"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {ok ? 'Pass' : 'Fail'} · {quality}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size="xsmall" />
      </button>
      {open && (
        <div
          className="c-validate-turn-score__detail"
          test-id="bobcat-validate-turn-score-detail"
        >
          {score.verdict.autoFail ? (
            <p className="c-validate-error">
              Auto-fail: {score.verdict.autoFail}
            </p>
          ) : (
            <div className="c-validate-turn-score__criteria">
              {verdictCriteria(score.verdict).map((c) => (
                <span
                  key={c.key}
                  title={score.reasons[c.key] ?? criterionHelp(c)}
                >
                  {c.short} <b>{score.verdict.scores[c.key]}</b>
                </span>
              ))}
            </div>
          )}
          {!ok && score.whyFailed ? (
            <p>
              <b>Why it failed.</b> {score.whyFailed}
            </p>
          ) : (
            score.summary && <p>{score.summary}</p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The whole conversation's score, for the chat in progress: scored on its
 * own when the chat ends, or now on request.
 */
const ConversationScoreBar: FC<{
  score: RunScore;
  onScore?: () => void;
  ended: boolean;
}> = ({ score, onScore, ended }) => {
  const action = (label: string) =>
    onScore && (
      <button
        type="button"
        className="c-validate-link"
        test-id="bobcat-validate-score-conversation"
        onClick={onScore}
      >
        {label}
      </button>
    );
  return (
    <div
      className="c-validate-convo-score"
      test-id="bobcat-validate-conversation-score"
    >
      <span className="c-validate-convo-score__label">Conversation</span>
      {score.status === 'idle' && (
        <>
          <span className="c-validate-convo-score__muted">
            {ended ? 'Not scored' : 'Scored when the chat ends'}
          </span>
          {action('Score now')}
        </>
      )}
      {score.status === 'pending' && (
        <span className="c-validate-convo-score__muted">Scoring…</span>
      )}
      {score.status === 'error' && (
        <>
          <span className="c-validate-convo-score__muted" title={score.message}>
            Couldn’t score it
          </span>
          {action('Try again')}
        </>
      )}
      {score.status === 'scored' && (
        <>
          <b className={passes(score.verdict) ? 'is-pass' : 'is-fail'}>
            {passes(score.verdict) ? 'Pass' : 'Fail'} ·{' '}
            {qualityScore(score.verdict)}
          </b>
          {action('Re-score')}
        </>
      )}
    </div>
  );
};

export const Bubbles: FC<{
  transcript: ValidateTurn[];
  botName: string;
  onPick?: (option: ValidateOption) => void;
  /** Each bot reply's own score, by transcript index. */
  turnScores?: Record<string, RunScore>;
  /** The chat's id — with it, each reply shows what the engine walked. */
  chatId?: string | null;
  /** A live webview's data and close, answered as the widget would. */
  webviewHandlers?: WebviewHandlers;
  /** Show the run's timeline above the conversation (a run's detail). */
  timeline?: boolean;
}> = ({
  transcript,
  botName,
  onPick,
  turnScores,
  chatId,
  webviewHandlers,
  timeline = false,
}) => {
  const lastBot = transcript.map((t) => t.role).lastIndexOf('bot');
  const trace = useEngineTrace(chatId, transcript);
  const hasSeeded = transcript.some((t) => t.seeded);
  const firstLive = hasSeeded ? transcript.findIndex((t) => !t.seeded) : -1;
  return (
    <div className="c-validate-bubbles">
      {timeline && (
        <RunTimeline transcript={transcript} byReply={trace.byReply} />
      )}
      {hasSeeded && (
        <div className="c-validate-divider">Seeded context · not scored</div>
      )}
      {transcript.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={i}>
          {i === firstLive && (
            <div className="c-validate-divider c-validate-divider--live">
              Simulated from here
            </div>
          )}
          <div
            className={`c-validate-bubble c-validate-bubble--${t.role}${t.seeded ? ' is-seeded' : ''}${t.system ? ' is-system' : ''}${t.agent ? ' is-agent' : ''}`}
          >
            <span className="c-validate-bubble__who">
              {t.role === 'user'
                ? 'You'
                : t.agent
                  ? 'Live agent · not scored'
                  : t.system
                    ? 'Platform message · not scored'
                    : botName}
              {typeof t.latencyMs === 'number' && (
                <b> · {(t.latencyMs / 1000).toFixed(2)}s</b>
              )}
            </span>
            {t.text && (
              <div className="c-validate-bubble__text">
                {t.role === 'user' ? (
                  renderIcons(t.text)
                ) : (
                  <BubbleMarkdown text={t.text} />
                )}
              </div>
            )}
            <EmbedNote types={t.embeds ?? []} />
            {t.submitted && (
              <div
                className="c-validate-submitted"
                test-id="bobcat-validate-submitted"
              >
                <span className="c-validate-submitted__head">
                  Submitted {t.submitted.form}
                </span>
                {t.submitted.fields.length ? (
                  <dl>
                    {t.submitted.fields.map((f) => (
                      <Fragment key={f.name}>
                        <dt>{f.name}</dt>
                        <dd title={f.masked ? 'Masked — not kept' : undefined}>
                          {f.value}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                ) : (
                  <span>No fields</span>
                )}
              </div>
            )}
            {t.webview && (
              <WebviewCard
                webview={t.webview}
                // Only the live chat's latest reply can still be filled in.
                live={!!onPick && i === lastBot}
                handlers={webviewHandlers}
              />
            )}
            {!!t.options?.length && (
              <div className="c-validate-bubble__options">
                {t.options.map((o) => (
                  <button
                    key={`${o.value}-${o.label}`}
                    type="button"
                    className="c-validate-option"
                    disabled={!onPick || i !== lastBot}
                    aria-label={withoutIcons(o.label) || undefined}
                    onClick={() => onPick?.(o)}
                  >
                    {renderIcons(o.label)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {t.role === 'bot' && turnScores?.[String(i)] && (
            <TurnScore score={turnScores[String(i)]} />
          )}
          {t.role === 'bot' && trace.byReply[String(i)] && (
            <EngineTrace requests={trace.byReply[String(i)]} />
          )}
        </Fragment>
      ))}
    </div>
  );
};

/** "forms.example.com" from a webview URL, for a webview the bot didn't name. */
const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return 'Webview';
  }
};

/** What the Test panel does with a live webview's messages. */
export interface WebviewHandlers {
  /** Data the webview hands over (`PYPESTREAM_WEBVIEW_DATA_PASSTHROUGH`). */
  onData: (data: Record<string, unknown> | unknown[], form: string) => void;
  /** The webview closed: send this as the customer's message. */
  onClose: (message: string) => void;
}

const PASSTHROUGH = 'PYPESTREAM_WEBVIEW_DATA_PASSTHROUGH:';
const AUTO_DISMISS = 'PYPESTREAM_WEBVIEW_AUTO_DISMISS';

/**
 * A webview the bot opened, framed in the chat the way the widget frames it
 * (`ps-pype-webview`: same sandbox and permissions), and answered the way the
 * widget answers it (`pype-shell-controller.ts`): data the webview passes
 * goes to the bot silently, and when it closes — the webview asks to, or the
 * tester clicks Close — its close message ("Time selected"; "Close" when it
 * has none) is sent as the customer's turn, which is what moves the flow on.
 * Only the latest reply's can be used; earlier ones, and a past run's, show
 * what was opened.
 */
const WebviewCard: FC<{
  webview: ValidateWebview;
  live: boolean;
  handlers?: WebviewHandlers;
}> = ({ webview, live, handlers }) => {
  const name = webview.label || hostOf(webview.url);
  const frame = useRef<HTMLIFrameElement>(null);
  // Only an https page on another origin is framed (PSTUDIO-4683).
  const framed = webviewFrame(webview.url, window.location.origin);
  const active = live && !!handlers && !!framed;
  const close = () => handlers?.onClose(webview.closeMessage || 'Close');

  // Only this card's own frame is listened to.
  useEffect(() => {
    if (!active) return undefined;
    const onMessage = (event: MessageEvent) => {
      if (!frame.current || event.source !== frame.current.contentWindow) {
        return;
      }
      const raw: unknown = event.data;
      if (typeof raw !== 'string') return;
      if (raw === AUTO_DISMISS) {
        handlers?.onClose(webview.closeMessage || 'Close');
        return;
      }
      if (!raw.startsWith(PASSTHROUGH)) return;
      try {
        const data: unknown = JSON.parse(raw.slice(PASSTHROUGH.length));
        if (data && typeof data === 'object') {
          handlers?.onData(data as Record<string, unknown> | unknown[], name);
        }
      } catch {
        // Not JSON: the widget drops it too.
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [active, handlers, webview.closeMessage, name]);

  return (
    <div className="c-validate-webview" test-id="bobcat-validate-webview">
      <div className="c-validate-webview__head">
        <Icon name="globe" size="xsmall" />
        <span className="c-validate-webview__name">{name}</span>
        {active && (
          <button
            type="button"
            className="c-validate-webview__toggle"
            test-id="bobcat-validate-webview-close"
            onClick={close}
          >
            Close
          </button>
        )}
      </div>
      {live && framed ? (
        <iframe
          ref={frame}
          className="c-validate-webview__frame"
          title={name}
          src={framed.src}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          allow={framed.allow}
        />
      ) : live ? (
        <TextCaption
          className="c-validate-webview__note"
          test-id="bobcat-validate-webview-refused"
        >
          Not opened here: a webview has to be a secure (https) page on another
          site.
        </TextCaption>
      ) : (
        <TextCaption className="c-validate-webview__note">
          Opened a webview here.
        </TextCaption>
      )}
    </div>
  );
};

/* ---- Test --------------------------------------------------------------- */

const STATUS_LABEL: Record<LiveChatState['status'], string> = {
  idle: 'Not started',
  connecting: 'Connecting…',
  ready: 'Live',
  waiting: 'Replying…',
  ended: 'Ended',
  error: 'Couldn’t connect',
};

const NO_MESSAGES: ReadonlyMap<number, string> = new Map();

export const TestPanel: FC<{
  channels: RunChannel[];
  channel: RunChannel | null;
  /** The chosen voice channel's Twilio line, for "Call me". */
  voiceLine?: VoiceLine | null;
  /** Each node's message, for a call's bot turns (the logs hold the node). */
  nodeMessages?: ReadonlyMap<number, string>;
  /** A phone call's conversation so far — saved as its run. */
  onCall?: (call: {
    chatId: string;
    transcript: ValidateTurn[];
    startedAt: number;
    done: boolean;
  }) => void;
  /** The current call's run score, once it has one. */
  callScore?: RunScore;
  onScoreCall?: () => void;
  onChannel: (appId: string) => void;
  versionLabel: string | null;
  noChannelReason: string;
  chat: LiveChatState;
  onSend: (text: string) => void;
  onPick: (option: ValidateOption) => void;
  /** A live webview in the chat: its data and its close. */
  webviewHandlers?: WebviewHandlers;
  onRestart: () => void;
  onEnd: () => void;
  /** Open the chat and show the greeting, before the first message. */
  onStart: () => void;
  /** This chat's run: each reply's score, and the conversation's. */
  turnScores?: Record<string, RunScore>;
  conversationScore?: RunScore;
  onScoreConversation?: () => void;
  /** Offer "Widget": test a chat channel in its real Pype Next widget. */
  widgetEnabled?: boolean;
}> = ({
  channels,
  channel,
  voiceLine = null,
  nodeMessages = NO_MESSAGES,
  onCall,
  callScore,
  onScoreCall,
  onChannel,
  versionLabel,
  noChannelReason,
  chat,
  onSend,
  onPick,
  webviewHandlers,
  onRestart,
  onEnd,
  onStart,
  turnScores,
  conversationScore,
  onScoreConversation,
  widgetEnabled = false,
}) => {
  const [draft, setDraft] = useState('');
  const [mode, setMode] = useState<'text' | 'widget'>('text');
  const [widgetDone, setWidgetDone] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const busy = chat.status === 'connecting' || chat.status === 'waiting';
  // Closed until the chat is started (Start chat), and after it ends. Once
  // open, typing stays open while the bot replies — a disabled box drops what
  // the tester types and loses focus. Only sending waits for the reply.
  const started = chat.status !== 'idle' && chat.status !== 'error';
  const canType = !!channel && started && chat.status !== 'ended';
  const canSend = canType && !busy;

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [chat.transcript.length, chat.status]);

  const submit = () => {
    if (!canSend || !draft.trim()) return;
    onSend(draft);
    setDraft('');
  };

  // A voice channel is tested on the phone ("Call me"), and the call's
  // conversation shows here as it happens, read from the logs.
  const voice = !!channel?.voiceChannelId;
  // A chat channel can also be tested in its real widget (Pype Next).
  const widget = !voice && !!channel && widgetEnabled && mode === 'widget';
  const call = useTestCall(voice ? voiceLine : null);
  const voiceCallsEnabled = useHealthCapability('voiceCalls');
  const callInFlight = call.status === 'placing' || call.live;
  const callView = useCallTranscript({
    integratorId: voice ? (voiceLine?.integratorId ?? null) : null,
    placedAt: call.placedAt,
    live: call.live || call.status === 'placing',
    nodeMessages,
  });

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [callView.transcript.length]);

  // The call is a run: saved as it fills in, scored once it's done.
  const {
    chatId: callChatId,
    transcript: callTurns,
    done: callDone,
  } = callView;
  useEffect(() => {
    if (!voice || !callChatId || !call.placedAt || !callTurns.length) return;
    onCall?.({
      chatId: callChatId,
      transcript: callTurns,
      startedAt: call.placedAt,
      done: callDone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice, callChatId, callTurns, callDone, call.placedAt]);

  const botName = channel?.name ?? 'Solution';

  return (
    <div className="c-validate-panel" test-id="bobcat-validate-test-panel">
      <PanelHead
        title="Test"
        icon="chat"
        // On a call the panel is the call: the how-to line goes.
        sub={
          voice && call.status
            ? undefined
            : 'Talk to the solution the way a customer would, on any of its channels. Each reply is scored as it comes in, and the whole conversation when it ends.'
        }
      >
        {channel && (
          <span
            className={`c-validate-pill c-validate-pill--${voice ? (call.live ? 'ready' : 'idle') : chat.status}`}
            test-id="bobcat-validate-chat-status"
          >
            {channel.env === 'live' ? 'Live' : 'Sandbox'}
            {versionLabel ? ` · ${versionLabel}` : ''} ·{' '}
            {voice
              ? call.live
                ? 'On a call'
                : 'Phone'
              : widget
                ? 'Widget'
                : STATUS_LABEL[chat.status]}
          </span>
        )}
      </PanelHead>

      <div className="c-validate-panel__fields">
        <label className="c-validate-field">
          <span>Channel</span>
          {channels.length ? (
            <ChannelSelect
              testId="bobcat-validate-test-channel"
              channels={channels}
              value={channel ? channelKey(channel) : null}
              onChange={onChannel}
            />
          ) : (
            <TextCaption>{noChannelReason}</TextCaption>
          )}
        </label>
        {channel && versionLabel && (
          <TextCaption test-id="bobcat-validate-tests-version">
            Tests {versionLabel}, what’s published to{' '}
            {channel.env === 'live' ? 'Live' : 'Sandbox'} — not your draft. To
            test your latest edits, publish them to Sandbox from Deploy.
          </TextCaption>
        )}
        {!voice && !!channel && widgetEnabled && (
          <div
            className="c-validate-segments"
            role="radiogroup"
            aria-label="Test in"
          >
            {(
              [
                ['text', 'Text'],
                ['widget', 'Widget'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                className={mode === value ? 'is-active' : ''}
                test-id={`bobcat-validate-mode-${value}`}
                onClick={() => setMode(value)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {voice ? (
          <VoiceCallGate
            enabled={voiceCallsEnabled}
            callInFlight={callInFlight}
          >
            <CallMe line={voiceLine} call={call} />
          </VoiceCallGate>
        ) : widget ? null : (
          <div className="c-validate-panel__chat-actions">
            <Button
              size="small"
              variant="ghost"
              test-id="bobcat-validate-restart"
              disabled={chat.status === 'idle' && !chat.transcript.length}
              onClick={onRestart}
            >
              <Icon name="refresh" slot="prefix" size="xsmall" />
              Restart
            </Button>
            <Button
              size="small"
              variant="ghost"
              test-id="bobcat-validate-end"
              disabled={!chat.chatId || chat.status === 'ended'}
              onClick={onEnd}
            >
              <Icon name="close" slot="prefix" size="xsmall" />
              End chat
            </Button>
          </div>
        )}
        {voice && callScore && (
          <ConversationScoreBar
            score={callScore}
            onScore={onScoreCall}
            ended={callView.done}
          />
        )}
        {widget && callScore && (
          <ConversationScoreBar
            score={callScore}
            onScore={onScoreCall}
            ended={widgetDone}
          />
        )}
        {!voice && !widget && conversationScore && (
          <ConversationScoreBar
            score={conversationScore}
            onScore={onScoreConversation}
            ended={chat.status === 'ended'}
          />
        )}
      </div>

      <div
        className={`c-validate-panel__chat${widget ? ' c-validate-panel__chat--widget' : ''}`}
        ref={scroller}
      >
        {channel && !versionLabel && (
          <p
            className="c-validate-notice"
            test-id="bobcat-validate-unpublished"
          >
            Nothing is published on{' '}
            {channel.env === 'live' ? 'Live' : 'Sandbox'} yet, so this channel
            has no solution to answer with — it replies with its fallback
            message and ends the chat. Publish from Deploy, then test here.
          </p>
        )}
        {widget && channel ? (
          <WidgetChat
            key={channelKey(channel)}
            channel={channel}
            nodeMessages={nodeMessages}
            onChat={onCall}
            onProgress={({ done }) => setWidgetDone(done)}
          />
        ) : voice ? (
          <>
            {callView.transcript.length === 0 ? (
              !call.placedAt && (
                <div className="c-validate-empty">
                  <span className="c-validate-empty__mark">
                    <Icon name="recording" size="small" />
                  </span>
                  <p>
                    Call your phone to talk to <b>{botName}</b> as a caller
                    would.
                  </p>
                </div>
              )
            ) : (
              <Bubbles
                transcript={callView.transcript}
                botName={botName}
                chatId={callView.chatId}
              />
            )}
            {callView.problem && (
              <p className="c-validate-error" role="alert">
                Couldn’t read the call from the logs: {callView.problem}
              </p>
            )}
          </>
        ) : (
          <>
            {chat.transcript.length === 0 ? (
              <div className="c-validate-empty">
                <span className="c-validate-empty__mark">
                  <Icon name="chat" size="small" />
                </span>
                <p>
                  {chat.status === 'ready' ? (
                    <>
                      The chat is open. <b>{botName}</b> didn’t send a greeting
                      — say something to begin.
                    </>
                  ) : (
                    <>
                      Start the chat to see <b>{botName}</b>’s greeting, then
                      talk to it as a real customer would. You’ll see each
                      reply, how long it took, and whether it passed.
                    </>
                  )}
                </p>
                {chat.status === 'connecting' ? (
                  <TextCaption>Connecting…</TextCaption>
                ) : (
                  !!channel &&
                  !started && (
                    <Button
                      size="small"
                      test-id="bobcat-validate-start-chat"
                      onClick={onStart}
                    >
                      Start chat
                    </Button>
                  )
                )}
              </div>
            ) : (
              <Bubbles
                transcript={chat.transcript}
                botName={botName}
                onPick={canSend ? onPick : undefined}
                webviewHandlers={canSend ? webviewHandlers : undefined}
                turnScores={turnScores}
                chatId={chat.chatId}
              />
            )}
            {chat.status === 'waiting' && (
              <div className="c-validate-typing" aria-live="polite">
                <i />
                <i />
                <i />
              </div>
            )}
            {chat.error && (
              <p
                className="c-validate-error"
                role="alert"
                test-id="bobcat-validate-chat-error"
              >
                {chat.error}
              </p>
            )}
            {chat.status === 'ended' && (
              <p className="c-validate-note">
                This chat has ended. Restart to begin a new one.
              </p>
            )}
          </>
        )}
      </div>

      {!voice && !widget && (
        <div className="c-validate-composer">
          <textarea
            test-id="bobcat-validate-composer"
            rows={3}
            placeholder={
              !channel
                ? 'No channel to test on'
                : started
                  ? 'Type a message to test…'
                  : 'Start the chat to type'
            }
            value={draft}
            disabled={!canType}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <IconButton
            name="arrow-up"
            size="small"
            variant="primary"
            aria-label="Send"
            test-id="bobcat-validate-send"
            disabled={!canSend || !draft.trim()}
            onClick={submit}
          />
        </div>
      )}
    </div>
  );
};

/* ---- Call me: a real test call over the phone ---------------------------- */

const CALL_ME_KEY = 'bobcat.validate.callMe';

/** "+14432414474" → "+1 (443) 241-4474"; anything else as given. */
const showPhone = (n: string) => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(n);
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : n;
};

/**
 * Rings the tester's phone from the solution's number and connects them to
 * it — the real voice path: Twilio, the channel's speech and voice, and a call
 * transfer to a phone agent.
 */
const CallMe: FC<{
  line: VoiceLine | null;
  call: ReturnType<typeof useTestCall>;
}> = ({ line, call }) => {
  const [to, setTo] = useState(() => {
    try {
      return localStorage.getItem(CALL_ME_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const busy = call.status === 'placing' || call.live;

  const start = () => {
    try {
      localStorage.setItem(CALL_ME_KEY, to);
    } catch {
      // Private window: the number just isn't remembered.
    }
    void call.call(to);
  };

  if (!line) {
    return (
      <div className="c-validate-call" test-id="bobcat-validate-call-me">
        <p className="c-validate-call__note">
          This voice channel has no Twilio account or number saved, so it can’t
          place a test call. Add them in the channel’s settings.
        </p>
      </div>
    );
  }

  return (
    <div className="c-validate-call" test-id="bobcat-validate-call-me">
      <span className="c-validate-call__title">Test on the phone</span>
      <p className="c-validate-call__note">
        We ring your phone from {showPhone(line.number)} and connect you to the
        solution — the real call, including transfers to a phone agent.
      </p>
      <div className="c-validate-call__row">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="c-validate-call__input"
          aria-label="Your phone number"
          placeholder="Your phone, e.g. (201) 555-0123"
          value={to}
          disabled={busy}
          onChange={(e) => setTo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && to.trim() && !busy) start();
          }}
          test-id="bobcat-validate-call-to"
        />
        {call.live ? (
          <Button
            size="small"
            variant="secondary"
            test-id="bobcat-validate-hang-up"
            onClick={() => void call.hangUp()}
          >
            Hang up
          </Button>
        ) : (
          <Button
            size="small"
            test-id="bobcat-validate-call"
            disabled={!to.trim() || busy}
            onClick={start}
          >
            <Icon name="recording" slot="prefix" size="xsmall" />
            Call me
          </Button>
        )}
      </div>
      {call.status && (
        <p
          className={`c-validate-call__status${call.live ? ' is-live' : ''}`}
          aria-live="polite"
          test-id="bobcat-validate-call-status"
        >
          {callStatusText(call.status, call.duration)}
        </p>
      )}
      {call.error && (
        <p className="c-validate-error" role="alert">
          {call.error}
        </p>
      )}
    </div>
  );
};

/* ---- How runs are scored ------------------------------------------------ */

const COUNT_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

/**
 * How runs are scored, on the solution's own rubric: its criteria, their
 * points and a worked example. The standard five unless it was edited.
 */
export const ScoringPanel: FC<{
  criteria: Criterion[];
  edited: boolean;
  onClose: () => void;
}> = ({ criteria, edited, onClose }) => {
  const example = scoreExample(criteria);
  const count = COUNT_WORDS[criteria.length] ?? String(criteria.length);
  const sameWeight = criteria.every((c) => c.weight === criteria[0]?.weight);
  return (
    <div className="c-validate-panel" test-id="bobcat-validate-scoring-panel">
      <PanelHead
        title="How runs are scored"
        sub={
          edited
            ? 'Every reply in Test and every simulated scenario is judged on this solution’s rubric, edited from the Generative AI Evaluation Rubric in Judges.'
            : 'Every reply in Test and every simulated scenario is judged on the Generative AI Evaluation Rubric.'
        }
        onClose={onClose}
      />
      <div className="c-validate-panel__scroll">
        <section className="c-validate-ev">
          <h3>The quality score</h3>
          {sameWeight ? (
            <p>
              Each reply is scored <b>1–5</b> on {count} criteria. Each score is
              multiplied by <b>{multiplier(criteria[0]?.weight ?? 0)}</b> and
              the {count} are added, for a score out of <b>100</b>. A run passes
              at <b>{passAt()}</b> or higher.
            </p>
          ) : (
            <p>
              Each reply is scored <b>1–5</b> on {count} criteria, each worth up
              to its own points. A 5 earns all of them and lower scores a share
              (the score × points ÷ 5); they add up to a score out of <b>100</b>
              . A run passes at <b>{passAt()}</b> or higher.
            </p>
          )}
          <div className="c-validate-ev__calc">
            <span className="c-validate-ev__calc-h">Example</span>
            <div className="c-validate-ev__calc-row">
              {example.rows.map((r) => (
                <span key={r.key}>
                  <em>{r.label}</em>
                  {r.level} × {r.times} = <b>{r.points}</b>
                </span>
              ))}
            </div>
            <div className="c-validate-ev__calc-total">
              {example.rows.map((r) => r.points).join(' + ')} ={' '}
              <b>{example.score}/100</b> —{' '}
              {example.score >= passAt() ? 'passes' : 'fails'}
            </div>
          </div>
        </section>

        <section className="c-validate-ev">
          <h3>The {count} criteria</h3>
          {criteria.map((c) => (
            <details key={c.key} className="c-validate-ev__crit">
              <summary>
                {c.group && (
                  <span className="c-validate-ev__group">{c.group}</span>
                )}
                <b>{c.label}</b>
                <span className="c-validate-ev__group">{c.weight} pts</span>
                <Icon name="chevron-down" size="xsmall" />
              </summary>
              <p>{c.definition}</p>
              {c.levels.some(Boolean) && (
                <ol className="c-validate-ev__levels">
                  {[...c.levels].reverse().map((level, i) => (
                    <li key={`${5 - i}`}>
                      <span className={`c-validate-lv c-validate-lv--${5 - i}`}>
                        {5 - i}
                      </span>
                      {level}
                    </li>
                  ))}
                </ol>
              )}
            </details>
          ))}
        </section>

        <section className="c-validate-ev">
          <h3>Auto-fail — scores 0</h3>
          <p>
            A reply that trips any of these fails outright, however well it does
            on the criteria.
          </p>
          {AUTO_FAIL.map((g) => (
            <div key={g.head} className="c-validate-ev__fail">
              <span className="c-validate-ev__fail-h">
                <Icon name="warning" size="xsmall" />
                {g.head}
              </span>
              <ul>
                {g.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="c-validate-ev">
          <h3>Also measured, not scored</h3>
          <div className="c-validate-ev__check">
            <Icon name="clock" size="xsmall" />
            <div>
              <b>Response time</b>
              <span>
                Customer’s message to the first reply, against a{' '}
                {RESPONSE_TARGET_S}s target.
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

/* ---- New scenario ------------------------------------------------------- */

const STEP_ORDER: ScenarioStep[] = ['writing', 'running', 'scoring'];

export const ScenarioPanel: FC<{
  channels: RunChannel[];
  defaultChannel: RunChannel | null;
  running: {
    step: ScenarioStep;
    transcript: ValidateTurn[];
    channel: RunChannel;
  } | null;
  agents: ExpectedAgent[];
  onRun: (input: {
    channel: RunChannel;
    description: string;
    expectation?: string;
    expectedAgent: ExpectedAgent | null;
    startAt: StartPosition;
  }) => void;
  onStop: () => void;
  onClose: () => void;
}> = ({
  channels,
  defaultChannel,
  running,
  agents,
  onRun,
  onStop,
  onClose,
}) => {
  const expectedAgentEnabled = useExpectedAgentEnabled();
  const [appId, setAppId] = useState<string | null>(
    defaultChannel?.appId ?? null
  );
  const [description, setDescription] = useState('');
  const [expectation, setExpectation] = useState('');
  const [agentId, setAgentId] = useState<string | null>(null);
  const [startAt, setStartAt] = useState<StartPosition>('beginning');
  const channel =
    channels.find((c) => c.appId === appId) ?? defaultChannel ?? null;

  const head = (
    <PanelHead
      title="New scenario"
      icon="stars"
      sub="Describe a situation and we’ll simulate the customer, run it on the channel you pick and score it."
      onClose={running ? undefined : onClose}
    />
  );

  if (running) {
    const at = STEP_ORDER.indexOf(running.step);
    const labels: Record<ScenarioStep, string> = {
      writing: 'Writing the simulated customer',
      running: `Running the conversation on ${running.channel.name}`,
      scoring: 'Scoring the conversation',
    };
    return (
      <div
        className="c-validate-panel"
        test-id="bobcat-validate-scenario-panel"
      >
        {head}
        <div className="c-validate-steps" aria-live="polite">
          {STEP_ORDER.map((s, i) => (
            <div
              key={s}
              className={`c-validate-step ${i < at ? 'is-done' : i === at ? 'is-doing' : ''}`}
            >
              <i />
              {labels[s]}
            </div>
          ))}
        </div>
        <div className="c-validate-panel__chat">
          <Bubbles
            transcript={running.transcript}
            botName={running.channel.name}
          />
        </div>
        <div className="c-validate-panel__actions">
          <Button size="small" variant="secondary" onClick={onStop}>
            Stop
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="c-validate-panel" test-id="bobcat-validate-scenario-panel">
      {head}
      <div className="c-validate-panel__scroll">
        <label className="c-validate-field">
          <span>Channel</span>
          {channels.length ? (
            <ChannelSelect
              testId="bobcat-validate-scenario-channel"
              channels={channels}
              value={channel?.appId ?? null}
              onChange={setAppId}
            />
          ) : (
            <TextCaption>This solution has no channel to run on.</TextCaption>
          )}
        </label>
        <label className="c-validate-field">
          <span>What happens in the conversation?</span>
          <textarea
            test-id="bobcat-validate-scenario-text"
            rows={4}
            value={description}
            placeholder="e.g. A customer asks to update their shipping address, but gives an incomplete street name"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <div className="c-validate-field">
          <span>Where does it start?</span>
          <StartSegments
            value={startAt}
            onChange={setStartAt}
            testIdPrefix="bobcat-validate-scenario-start"
          />
          <small>
            {START_POSITIONS.find((p) => p.value === startAt)?.hint}
            {startAt !== 'beginning' &&
              ' The earlier messages are replayed to the solution first, as context, and aren’t scored.'}
          </small>
        </div>
        <label className="c-validate-field">
          <span>What should the solution do?</span>
          <small>Optional. This is what the result is scored against.</small>
          <input
            test-id="bobcat-validate-scenario-expect"
            value={expectation}
            placeholder="e.g. Validate the address and confirm it back"
            onChange={(e) => setExpectation(e.target.value)}
          />
        </label>
        {expectedAgentEnabled && agents.length > 0 && (
          <label className="c-validate-field">
            <span>What should handle it?</span>
            <small>
              Optional. Routing accuracy compares this with the agent that
              actually handled it.
            </small>
            <AgentSelect
              testId="bobcat-validate-scenario-agent"
              agents={agents}
              value={agentId}
              onChange={setAgentId}
            />
          </label>
        )}
      </div>
      <div className="c-validate-panel__actions">
        <Button
          size="small"
          variant="primary"
          test-id="bobcat-validate-run-scenario"
          disabled={!channel || !description.trim()}
          onClick={() =>
            channel &&
            onRun({
              channel,
              description: description.trim(),
              expectation: expectation.trim() || undefined,
              expectedAgent: agents.find((a) => a.id === agentId) ?? null,
              startAt,
            })
          }
        >
          <Icon name="play" slot="prefix" size="xsmall" />
          Run scenario
        </Button>
        <Button size="small" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
